import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppBar, Box, Toolbar, IconButton, Typography, MenuItem, Menu, Tooltip, Avatar, Divider, useTheme, } from '@mui/material';

import { Key, KeyboardArrowDown, Logout } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import MenuIcon from '@mui/icons-material/Menu';

import { RootState } from '../../../redux/store';
import { useSettings } from '../../../providers/SettingsProvider';
import { NAVIGATE_AUTH } from '../../../constant';
import useResponsive from '../../../hooks/useResponsive';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import ImageWithLoader from '../../common/ImageWithLoader';
import { openDialog } from '../../../redux/reducer/dialogSlice';

export default function AdminTopBar() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isTablet, isDesktop, isMobile } = useResponsive();
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const theme = useTheme();

    const authUser = useSelector((state: RootState) => state.authUser);

    const { setIsDashboardDrawerOpened } = useSettings();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
        React.useState<null | HTMLElement>(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleProfileNavigate = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    }
    const formatUserType = (userType?: string) => {
        if (!userType) return "";
        return userType.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };


    const handleLogout = React.useCallback(() => {
        navigate(NAVIGATE_AUTH.LOGOUT_PAGE);
    }, [navigate])

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else if (document.exitFullscreen) {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleuserdevelopment = () => {
        dispatch(showSnackbar({ type: 'warning', message: 'Feature coming soon. Stay tuned!' }));
    }


    const handleChangePassword = () => {
        setAnchorEl(null)
        dispatch(openDialog({ title: `Change Password`, moduleType: 'user', type: 'ChangePasswordDialog', payload: authUser?.user_details?.name, size: 'xs' }));
        
    };

    const menuId = 'primary-search-account-menu';
    const renderMenu = (<Menu anchorEl={anchorEl} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} id={menuId} keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleProfileNavigate}>
            <Avatar sx={{ width: 25, height: 25, mr: 1, bgcolor: 'primary.light' }} />
            <Box>
                <Typography variant='body1' sx={{ lineHeight: 1 }}>{authUser.user_details ? authUser.user_details.name : ''}</Typography>
                <Typography variant='caption' sx={{ fontSize: '0.7rem' }}>{formatUserType(authUser?.user_details?.user_type) || 'NA'}</Typography>
            </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleChangePassword}><Avatar sx={{ width: 22, height: 22, mr: 1, bgcolor: '#fff' }}><Key sx={{ color: '#000', fontSize: 18 }} /></Avatar>Change Password</MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}><Avatar sx={{ width: 22, height: 22, mr: 1, bgcolor: '#fff' }}><Logout sx={{ color: '#000', fontSize: 18 }} /></Avatar>Logout</MenuItem>
    </Menu>);

    const mobileMenuId = 'primary-search-account-menu-mobile';
    const renderMobileMenu = (
        <Menu anchorEl={mobileMoreAnchorEl} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} id={mobileMenuId} keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }} open={isMobileMenuOpen} onClose={handleMobileMenuClose}>
            <MenuItem onClick={handleProfileNavigate}>
                <Avatar sx={{ width: 25, height: 25, mr: 1, bgcolor: 'primary.light' }} />
                <Box>
                    <Typography variant='body1' sx={{ lineHeight: 1 }}>{authUser.user_details ? authUser.user_details.name : ''}</Typography>
                    <Typography variant='caption' sx={{ fontSize: '0.7rem' }}>{formatUserType(authUser?.user_details?.user_type) || 'NA'}</Typography>
                </Box>
            </MenuItem>
            <MenuItem onClick={handleChangePassword}><Avatar sx={{ width: 22, height: 22, mr: 1, bgcolor: '#fff' }}><Key sx={{ color: '#000', fontSize: 18 }} /></Avatar>Change Password</MenuItem>
            <Divider />
            <MenuItem onClick={handleuserdevelopment}><Avatar sx={{ width: 22, height: 22, mr: 1, bgcolor: '#fff' }}><ContactPhoneOutlinedIcon sx={{ color: '#000', fontSize: 18 }} /></Avatar>Admin Support </MenuItem>
            <MenuItem onClick={handleLogout}><Avatar sx={{ width: 22, height: 22, mr: 1, bgcolor: '#fff' }}><Logout sx={{ color: '#000', fontSize: 18 }} /></Avatar>Logout</MenuItem>
        </Menu>
    );

    return (<Box sx={{ zIndex: 101 }}>
        <AppBar elevation={5} sx={{ backgroundColor: 'common.white' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '90px !important' }}>
                <Box display="flex" alignItems="center" sx={{ flexGrow: 1, cursor: "pointer" }} >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => setIsDashboardDrawerOpened()} edge="start" color="inherit" aria-label="open drawer" sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'transparent' } }} >
                            <MenuIcon fontSize='large' />
                        </IconButton>
                    </Box>
                    <Box sx={{ borderRight: { xs: "none", md: `2px solid ${theme.palette.primary.main}`, borderRadius: 3 }, height: "60px", mx: 1 }} />
                    <ImageWithLoader src="/logo/logo-main.jpeg" alt="Mukhyamantri Rugna Mitra Logo" style={{ width: isMobile ? 50 : 75, height: isMobile ? 50 : 75, cursor: 'pointer' }} onClick={() => navigate("/")} />
                    <Box sx={{ maxWidth: "500px", flexShrink: 1, p: { xs: 1, md: 1 } }}>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main} sx={{ fontSize: { xs: "0.9rem", md: "1.8rem" } }}>Mukhyamantri Rugna Mitra</Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary} sx={{ display: { xs: "0.1rem", md: "block" }, fontSize: { xs: "0.5rem", md: "0.9rem" } }}>Your health, our priority – Find the nearest hospital instantly!</Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {isDesktop && (
                        <>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} arrow>
                                    <IconButton
                                        color="primary"
                                        onClick={toggleFullscreen}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                    >
                                        {isFullscreen ? <FullscreenExitIcon fontSize='large' /> : <FullscreenIcon fontSize='large' />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ mx: 1.5, height: 40, alignSelf: 'center' }} />

                            {/* Profile Section */}
                            <Tooltip title="View Profile" arrow>
                                <Box onClick={handleProfileMenuOpen} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, borderRadius: 2, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { backgroundColor: 'action.hover', }, }} >
                                    <IconButton size="small" aria-controls={anchorEl ? 'profile-menu' : undefined} aria-haspopup="true" aria-expanded={Boolean(anchorEl)} sx={{ p: 0, '&:hover': { backgroundColor: 'transparent', } }} >
                                        <Avatar
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                bgcolor: 'info.light',
                                                // fontSize: '0.95rem',
                                                // fontWeight: 700,
                                                border: '2px solid',
                                                borderColor: 'primary.lighter',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                }
                                            }}
                                        >
                                            {authUser?.user_details?.name?.charAt(0).toUpperCase() || 'A'}
                                        </Avatar>
                                    </IconButton>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                lineHeight: 1.2,
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                fontSize: '0.975rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 150
                                            }}
                                        >
                                            {authUser?.user_details?.name || 'Guest User'}
                                        </Typography>

                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor: 'success.main',
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                    lineHeight: 1,
                                                    fontWeight: 500
                                                }}
                                            >
                                                {formatUserType(authUser?.user_details?.user_type) || 'NA'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Dropdown indicator */}
                                    <Box
                                        sx={{
                                            ml: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            opacity: 0.6,
                                            transition: 'transform 0.2s ease',
                                            transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                    >
                                        <KeyboardArrowDown fontSize="small" />
                                    </Box>
                                </Box>
                            </Tooltip>
                        </>
                    )}

                    {/* Mobile Menu */}
                    {(isMobile || isTablet) && (
                        <IconButton
                            size="large"
                            aria-label="show more"
                            aria-controls={mobileMenuId}
                            aria-haspopup="true"
                            onClick={handleMobileMenuOpen}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
        {renderMobileMenu}
        {renderMenu}
    </Box>);
}